import {TableEmpty} from '@/ui/index'

import {PermissionButtonOrg, TableCell, TimeAgoText} from '@/ui/index'

import {TableRow} from '@/ui/index'

import {TableBody} from '@/ui/index'

import {Box, Text} from '@sanity/ui'
import {useConfirmDialog} from '@/ui/index'

import {Table} from '@/ui/index'
import {Robot} from '@sanity/access-api'
import {Flex} from '@sanity/ui'
import {useState} from 'react'
import {TableHeader} from '@/types/index'
import {TrashIcon} from '@sanity/icons'
import {useDeleteOrgToken} from './queries'
import {useCurrentScopeContext} from '@/context/useCurrentScopeContext'
import {BadgeStack} from '@/ui/ui-components/generic/badge-stack'
import {capitalize} from 'lodash'

const roleDisplayNames = {
  'sdk-app-editor-robot': 'Manage SDK Apps',
  'deploy-studio-robot': 'Deploy All Studios',
  'deploy-studio': 'Deploy Studio',
  'media-library-viewer-robot': 'Media Library Viewer',
  'media-library-editor-robot': 'Media Library Editor',
  'canvas-viewer-robot': 'Canvas Viewer',
  'canvas-editor-robot': 'Canvas Editor',
}

const headers: TableHeader[] = [
  {
    id: 'name',
    label: 'Name',
    columnWidth: 2,
  },
  {
    id: 'permissions',
    label: 'Permissions',
    columnWidth: 2,
  },
  {
    id: 'created',
    label: 'Created',
    columnWidth: 1,
    hideOnMobile: true,
  },
  {
    id: 'delete',
    label: 'Delete',
    columnWidth: 0,
    disableTabButtonRender: true,
  },
]
export const TokensTable = ({tokens, loading}: {tokens: Robot[]; loading: boolean}) => {
  const {mutateAsync: deleteToken, isPending: isDeleting} = useDeleteOrgToken()

  const {Dialog, hideDialog, showDialog} = useConfirmDialog()
  const [itemToDelete, setItemToDelete] = useState<Robot | null>(null)

  const handleDeleteToken = (token: Robot) => {
    showDialog()
    setItemToDelete(token)
  }

  const isEmpty = tokens.length === 0

  const {org} = useCurrentScopeContext()
  const projects = org?.projects || []

  const handleDeleteConfirm = async () => {
    if (itemToDelete) {
      await deleteToken(itemToDelete)
      setItemToDelete(null)
      hideDialog()
    }
  }

  return (
    <>
      <Table loading={loading} headers={isEmpty ? [] : headers} loadingText="Loading API tokens...">
        <TableBody>
          {tokens.map((token, index) => (
            <TableRow
              key={token.id}
              selected={false}
              paddingX={2}
              paddingY={3}
              borderBottom={index + 1 !== tokens.length}
            >
              <TableCell>
                <Flex paddingY={3} sizing="border">
                  <Text style={{wordBreak: 'break-word'}} size={1}>
                    {token.label}
                  </Text>
                </Flex>
              </TableCell>
              <TableCell>
                <Flex paddingY={3} sizing="border">
                  <BadgeStack
                    maxLength={5}
                    items={token.memberships.flatMap((membership) =>
                      membership.roleNames.map((roleName) => {
                        const baseRoleName = roleDisplayNames[roleName] || capitalize(roleName)
                        const displayName =
                          membership.resourceType === 'project'
                            ? `${baseRoleName} (${projects.find((p) => p.id === membership.resourceId)?.displayName || membership.resourceId})`
                            : baseRoleName

                        return {
                          title: displayName,
                        }
                      })
                    )}
                  />
                </Flex>
              </TableCell>
              <TableCell>
                <Flex paddingY={3} sizing="border">
                  <TimeAgoText date={token.createdAt} muted size={1} />
                </Flex>
              </TableCell>
              <TableCell>
                <Flex justify="flex-end" style={{width: '100%'}}>
                  <Box>
                    <PermissionButtonOrg
                      disabled={isDeleting}
                      mode="bleed"
                      tone="critical"
                      icon={TrashIcon}
                      onClick={() => handleDeleteToken(token)}
                      permissions={[
                        {permissionName: 'sanity.organization.tokens', grantName: 'delete'},
                      ]}
                      action="Cannot delete API tokens"
                    />
                  </Box>
                </Flex>
              </TableCell>
            </TableRow>
          ))}
          {isEmpty && (
            <TableEmpty title="There are no API tokens yet" description="Maybe try creating one?" />
          )}
        </TableBody>
      </Table>
      {itemToDelete && (
        <Dialog
          id="delete-token-dialog"
          header="Delete API token"
          title="Do you really want to delete this API token?"
          description="This action cannot be undone."
          onClose={hideDialog}
          onConfirm={handleDeleteConfirm}
          confirmButtonProps={{text: 'Delete'}}
          loading={isDeleting}
        />
      )}
    </>
  )
}
